import { useEffect, useState } from "react";
import { KeyRound, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  secretariat: "Urusetia",
  president: "Presiden",
  treasurer: "Bendahari",
  committee: "AJK",
  verifier: "Pengesah",
};

export default function PenggunaStaf() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resettingId, setResettingId] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/admin/staff-users").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const resetPassword = async (userId) => {
    setResettingId(userId);
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      setResetResult(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setResettingId(null);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(resetResult.new_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="admin-pengguna-staf-page">
      <h1 className="font-display text-2xl font-bold text-piks-black mb-1">Pengguna Staf</h1>
      <p className="text-sm text-piks-muted mb-6">Senarai akaun log masuk staf/admin PPPS. Reset kata laluan jika staf terlupa.</p>

      {loading ? (
        <p className="text-piks-muted text-sm">Memuatkan...</p>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.id} className="p-5 rounded-xl bg-white border border-piks-border flex items-center justify-between gap-4" data-testid={`staff-user-${u.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-piks-black">{u.name}</p>
                <p className="text-sm text-piks-muted">{u.email}</p>
                <p className="text-[11px] text-piks-red font-medium mt-1 uppercase tracking-wide">{ROLE_LABELS[u.role] || u.role}</p>
              </div>
              <Button size="sm" variant="outline" disabled={resettingId === u.id} onClick={() => resetPassword(u.id)} data-testid={`staff-user-reset-password-${u.id}`} className="shrink-0">
                <KeyRound className="w-3.5 h-3.5 mr-1.5" /> {resettingId === u.id ? "Menetapkan..." : "Reset Kata Laluan"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent data-testid="staff-reset-password-result-dialog">
          <DialogHeader>
            <DialogTitle>Kata Laluan Baharu Ditetapkan</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-piks-muted">
            Kata laluan baharu untuk <strong className="text-piks-black">{resetResult?.name}</strong> ({resetResult?.email}) hanya dipaparkan sekali. Sila salin dan berikan kepada staf secara selamat.
          </p>
          <div className="flex items-center gap-2 bg-piks-ivory rounded-lg p-3 border border-piks-border">
            <code className="flex-1 font-mono text-sm text-piks-black break-all" data-testid="staff-reset-password-value">{resetResult?.new_password}</code>
            <Button size="sm" variant="outline" onClick={copyPassword} data-testid="staff-reset-password-copy-btn">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
